import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import JsonLd from "../../components/base/JsonLd";
import { StackCard } from "../../components/feature/ArticleCard";
import { articles, platforms } from "../../mocks/gossip";
import type { Article } from "../../types";
import useSeo from "../../hooks/useSeo";

const BASE = "https://qihang77.cn";

export default function Channel() {
  const { slug = "weibo" } = useParams();
  const [params] = useSearchParams();
  const platform = platforms.find((p) => p.slug === slug) ?? platforms[0];
  const [cat, setCat] = useState("全部");
  const [sort, setSort] = useState<"hot" | "new">("hot");

  useSeo({
    title: `${platform.name}明星八卦-明星爆料`,
    description: `${platform.name}是明星爆料聚合平台的重要信源。这里有关于${platform.name}的最新明星八卦、高能爆料与事件追踪，帮你把散落的信息整理成完整脉络。`,
    keywords: `明星爆料,${platform.name},明星八卦,${platform.short}爆料`,
    path: `/channel/${platform.slug}`,
    type: "collection",
  });

  const base = useMemo(() => articles.filter((a: Article) => a.source === platform.slug), [platform.slug]);
  const cats = useMemo(() => ["全部", ...Array.from(new Set(base.map((a) => a.category)))], [base]);
  const kw = (params.get("q") || "").trim();

  const list = useMemo(() => {
    let arr = base;
    if (cat !== "全部") arr = arr.filter((a) => a.category === cat);
    if (kw) arr = arr.filter((a) => [a.title, a.summary, ...a.tags].join(" ").includes(kw));
    const sorted = [...arr];
    if (sort === "hot") sorted.sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", "")));
    else sorted.sort((a, b) => a.time.localeCompare(b.time));
    return sorted;
  }, [base, cat, kw, sort]);

  const crumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: BASE },
      { "@type": "ListItem", position: 2, name: platform.name, item: `${BASE}/channel/${platform.slug}` },
    ],
  };
  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${platform.name}明星八卦`,
    url: `${BASE}/channel/${platform.slug}`,
    inLanguage: "zh-CN",
  };

  return (
    <>
      <JsonLd data={[crumb, collection]} />
      <Navbar />
      <main className="bg-background-50 pt-16 md:pt-[72px]">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* 面包屑 */}
          <nav aria-label="面包屑" className="py-5">
            <ol className="flex items-center gap-1.5 text-xs text-foreground-400 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-sm" /></li>
              <li className="text-foreground-700 whitespace-nowrap">{platform.name}</li>
            </ol>
          </nav>

          {/* 频道头 */}
          <section className="rounded-2xl bg-foreground-950 text-background-50 px-6 md:px-10 py-8 md:py-10 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-primary-600/20 blur-2xl" aria-hidden="true"></div>
            <div className="absolute right-24 bottom-0 opacity-10 text-[120px] leading-none select-none" aria-hidden="true">
              <i className={`${platform.icon} font-thin`}></i>
            </div>
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
              <span className="w-16 h-16 shrink-0 rounded-2xl bg-primary-600 flex items-center justify-center shadow-sm">
                <i className={`${platform.icon} text-3xl`} aria-hidden="true"></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-background-200/70 tracking-widest uppercase mb-1 whitespace-nowrap">{platform.short} 频道</p>
                <h1 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">{platform.name}</h1>
                <p className="text-sm text-background-200/85 mt-2 max-w-2xl leading-relaxed">{platform.blurb} 我们持续为你聚合来自{platform.short}的高能爆料。</p>
              </div>
              <div className="flex items-center gap-6 text-background-100 shrink-0">
                <div className="text-center">
                  <p className="font-heading font-bold text-2xl text-accent-400">{base.length}</p>
                  <p className="text-xs text-background-200/70 mt-0.5">本频道条数</p>
                </div>
                <div className="w-px h-10 bg-background-800"></div>
                <div className="text-center">
                  <p className="font-heading font-bold text-2xl text-accent-400">
                    {Math.round(base.reduce((s, a) => s + Number(a.views.replace("万", "")), 0))}万
                  </p>
                  <p className="text-xs text-background-200/70 mt-0.5">累计阅读</p>
                </div>
              </div>
            </div>
          </section>

          {/* 其它平台快捷切换 */}
          <div className="flex items-center gap-2 my-6 flex-wrap">
            <span className="text-xs text-foreground-400 whitespace-nowrap">切换频道：</span>
            {platforms.map((p) => (
              <Link
                key={p.slug}
                to={`/channel/${p.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition whitespace-nowrap ${
                  p.slug === platform.slug
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${p.icon} ${p.slug === platform.slug ? "" : "text-primary-500"}`} aria-hidden="true"></i>
                {p.short}
              </Link>
            ))}
          </div>

          {/* 筛选与排序 */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                    cat === c ? "bg-primary-600 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 rounded-full bg-background-100 p-1 self-start lg:self-auto">
              {(["hot", "new"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSort(s)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                    sort === s ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-500"
                  }`}
                >
                  {s === "hot" ? "最热" : "最新"}
                </button>
              ))}
            </div>
          </div>

          {kw && (
            <p className="text-sm text-foreground-500 mb-4">
              频道内搜索 “<span className="text-primary-700 font-medium">{kw}</span>” 共找到 <b className="text-foreground-900">{list.length}</b> 条相关爆料
            </p>
          )}

          {list.length === 0 ? (
            <div className="rounded-2xl bg-background-100 px-6 py-16 text-center">
              <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-4 text-foreground-500">没有找到相关爆料，换个关键词试试，或到别的频道看看。</p>
              <Link to={`/channel/${platform.slug}`} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-600 text-background-50 px-5 py-2 text-sm font-medium hover:bg-primary-700 whitespace-nowrap">
                <i className="ri-refresh-line" aria-hidden="true"></i> 重置查看全部
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-14">
              {list.map((a) => (
                <StackCard key={a.id} article={a} />
              ))}
            </div>
          )}

          <p className="text-center text-xs text-foreground-400 pb-4">—— 已经到底啦，欢迎分享给更多吃瓜小伙伴 ——</p>
        </div>
      </main>
      <Footer />
    </>
  );
}